import React, { FC, ComponentType, Fragment, createContext, useState } from 'react';

import { INotify, NotifyTypes } from 'types/app';
import { DEFAULT_NOTIFY_OPTIONS } from 'constants/index';
import { Notify } from 'components';

export interface IWithNotify {
  notify: {
    info: (props?: INotify) => void;
    error: (props?: INotify) => void;
    mutationError: (props?: INotify) => void;
    queryError: (props?: INotify) => void;
  };
}

interface IState extends INotify {
  show: boolean;
  type: NotifyTypes;
}

export const NotifyContext = createContext<IWithNotify>({
  notify: null,
});

const NotifyProvider: FC = ({ children }) => {
  const [notifyState, setNotifyState] = useState<IState>({
    type: 'info',
    show: false,
    ...DEFAULT_NOTIFY_OPTIONS,
  });

  function handleClose() {
    setNotifyState({
      ...notifyState,
      show: false,
    });
  }

  function info(props?: INotify) {
    setNotifyState({
      type: 'info',
      show: true,
      ...DEFAULT_NOTIFY_OPTIONS,
      ...props,
    });
  }

  function error(props?: INotify) {
    setNotifyState({
      type: 'error',
      show: true,
      duration: null,
      ...DEFAULT_NOTIFY_OPTIONS,
      ...props,
    });
  }

  function mutationError(props?: INotify) {
    setNotifyState({
      type: 'mutationError',
      show: true,
      duration: null,
      ...DEFAULT_NOTIFY_OPTIONS,
      ...props,
    });
  }

  function queryError(props?: INotify) {
    setNotifyState({
      type: 'queryError',
      show: true,
      duration: null,
      ...DEFAULT_NOTIFY_OPTIONS,
      ...props,
    });
  }

  return (
    <NotifyContext.Provider value={{ notify: { info, error, mutationError, queryError } }}>
      <Fragment>
        <Notify onClose={handleClose} {...notifyState} />
        {children}
      </Fragment>
    </NotifyContext.Provider>
  );
};

const withNotify = <T extends {}>(ChildComponent: ComponentType<T & IWithNotify>) => (props: T) => (
  <NotifyContext.Consumer>
    {(providerProps: IWithNotify) => <ChildComponent {...props} {...providerProps} />}
  </NotifyContext.Consumer>
);

export { NotifyProvider, withNotify };
